import { Button } from '@/components/ui/button';
import { joinBusinessWaitlist } from '@/services/placeholders';
import { BookCheck, Store, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface WaitlistModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function WaitlistModal({ open, onOpenChange }: WaitlistModalProps) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        shopName: '',
    });

    // Lock body scroll when modal is open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.email.trim()) return;

        setIsSubmitting(true);
        try {
            const result = await joinBusinessWaitlist(formData);
            if (result.success) {
                toast.success(t('pricing.waitlist.successTitle'), {
                    description: t('pricing.waitlist.successMessage'),
                    duration: 5000,
                });
                onOpenChange(false);
                setFormData({ name: '', email: '', shopName: '' });
            }
        } catch {
            toast.error(t('pricing.waitlist.errorMessage'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!open) return null;

    // Use createPortal to render at document.body level,
    // escaping parent CSS transform/overflow constraints from framer-motion
    return createPortal(
        <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => onOpenChange(false)}
            />

            {/* Modal Content */}
            <div
                className="relative max-h-[90vh] overflow-y-auto bg-[#FDF8F3] border border-[#E5DCD3] rounded-[24px] p-6 sm:p-8 shadow-2xl"
                style={{ zIndex: 10000, width: '90%', maxWidth: '512px' }}
            >
                {/* Close Button */}
                <button
                    onClick={() => onOpenChange(false)}
                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#6B5344]/10 flex items-center justify-center text-[#6B5344] hover:bg-[#6B5344]/20 transition-colors"
                    aria-label="Fechar"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Header */}
                <div className="text-center mb-6 pr-6">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#6B5344] to-[#2D1810] flex items-center justify-center mx-auto mb-3">
                        <Store className="w-7 h-7 text-white" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-[#2D1810] mb-2">
                        {t('pricing.waitlist.title')}
                    </h2>
                    <p className="text-sm text-[#6B5344]">
                        {t('pricing.waitlist.description')}
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Name */}
                    <div>
                        <label
                            htmlFor="waitlist-name"
                            className="block text-sm font-medium text-[#2D1810] mb-1.5"
                        >
                            {t('pricing.waitlist.nameLabel')} *
                        </label>
                        <input
                            id="waitlist-name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, name: e.target.value }))
                            }
                            placeholder={t('pricing.waitlist.namePlaceholder')}
                            className="w-full px-4 py-3 rounded-xl border border-[#E5DCD3] bg-white text-[#2D1810] placeholder:text-[#9C8979] focus:outline-none focus:ring-2 focus:ring-[#6B5344]/30 focus:border-[#6B5344] transition-all text-base"
                        />
                    </div>

                    {/* Email */}
                    <div>
                        <label
                            htmlFor="waitlist-email"
                            className="block text-sm font-medium text-[#2D1810] mb-1.5"
                        >
                            {t('pricing.waitlist.emailLabel')} *
                        </label>
                        <input
                            id="waitlist-email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, email: e.target.value }))
                            }
                            placeholder={t('pricing.waitlist.emailPlaceholder')}
                            className="w-full px-4 py-3 rounded-xl border border-[#E5DCD3] bg-white text-[#2D1810] placeholder:text-[#9C8979] focus:outline-none focus:ring-2 focus:ring-[#6B5344]/30 focus:border-[#6B5344] transition-all text-base"
                        />
                    </div>

                    {/* Shop Name (Optional) */}
                    <div>
                        <label
                            htmlFor="waitlist-shop"
                            className="block text-sm font-medium text-[#2D1810] mb-1.5"
                        >
                            {t('pricing.waitlist.shopLabel')}
                        </label>
                        <input
                            id="waitlist-shop"
                            type="text"
                            value={formData.shopName}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, shopName: e.target.value }))
                            }
                            placeholder={t('pricing.waitlist.shopPlaceholder')}
                            className="w-full px-4 py-3 rounded-xl border border-[#E5DCD3] bg-white text-[#2D1810] placeholder:text-[#9C8979] focus:outline-none focus:ring-2 focus:ring-[#6B5344]/30 focus:border-[#6B5344] transition-all text-base"
                        />
                    </div>

                    {/* Submit */}
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full rounded-full py-6 text-base font-semibold bg-[#6B5344] hover:bg-[#5A4538] text-white transition-all duration-300 hover:scale-[1.02] hover:shadow-lg disabled:opacity-50"
                    >
                        {isSubmitting ? (
                            <span className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                {t('pricing.waitlist.submitting')}
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                <BookCheck className="w-4 h-4" />
                                {t('pricing.waitlist.submitButton')}
                            </span>
                        )}
                    </Button>
                </form>
            </div>
        </div>,
        document.body
    );
}
